import React, { useState, useEffect, useRef } from 'react';
import './ExerciseSteps.css';

const BreathingExerciseStep = ({ step, onContinue }) => {
  const [currentPhase, setCurrentPhase] = useState('inhale'); // inhale, hold, exhale
  const [currentRound, setCurrentRound] = useState(1);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  
  const { inhaleSeconds, holdSeconds, exhaleSeconds, rounds } = step.content;
  const intervalRef = useRef(null);

  const startExercise = () => {
    setIsActive(true);
    setCurrentRound(1);
    setCurrentPhase('inhale');
    setTimeLeft(inhaleSeconds);
  };

  useEffect(() => {
    if (!isActive) return;

    intervalRef.current = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          // Phase completed, move to next phase
          if (currentPhase === 'inhale') {
            setCurrentPhase('hold');
            return holdSeconds;
          } else if (currentPhase === 'hold') {
            setCurrentPhase('exhale');
            return exhaleSeconds;
          } else if (currentPhase === 'exhale') {
            // Round completed
            if (currentRound < rounds) {
              setCurrentRound(currentRound + 1);
              setCurrentPhase('inhale');
              return inhaleSeconds;
            } else {
              // Exercise completed
              setIsActive(false);
              setIsComplete(true);
              return 0;
            }
          }
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isActive, currentPhase, currentRound, inhaleSeconds, holdSeconds, exhaleSeconds, rounds]);

  const getPhaseText = () => {
    switch (currentPhase) {
      case 'inhale':
        return 'Inhalá';
      case 'hold':
        return 'Mantené';
      case 'exhale':
        return 'Exhalá';
      default:
        return '';
    }
  };

  const getPhaseEmoji = () => {
    switch (currentPhase) {
      case 'inhale':
        return '🫁';
      case 'hold':
        return '⏸️';
      case 'exhale':
        return '💨';
      default:
        return '';
    }
  };

  if (!isActive && !isComplete) {
    return (
      <div className="step-container">
        <div className="step-content">
          <h2 className="step-title">{step.title}</h2>
          <div className="breathing-instructions">
            <p>Vamos a hacer {rounds} rondas de respiración:</p>
            <ul>
              <li>Inhalá por {inhaleSeconds} segundos</li>
              <li>Mantené por {holdSeconds} segundos</li>
              <li>Exhalá por {exhaleSeconds} segundos</li>
            </ul>
          </div>
          <div className="breathing-start">
            <button className="step-button primary" onClick={startExercise}>
              Empezar
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isComplete) {
    return (
      <div className="step-container">
        <div className="step-content">
          <h2 className="step-title">¡Excelente!</h2>
          <div className="breathing-complete">
            <p>Completaste {rounds} rondas de respiración consciente.</p>
            <p>¿Cómo te sentís ahora?</p>
          </div>
          <div className="step-actions">
            <button className="step-button primary" onClick={onContinue}>
              Continuar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="step-container">
      <div className="step-content">
        <h2 className="step-title">{step.title}</h2>
        
        <div className="breathing-display">
          <div className="breathing-circle">
            <div className="breathing-emoji">{getPhaseEmoji()}</div>
            <div className="breathing-phase">{getPhaseText()}</div>
            <div className="breathing-timer">{timeLeft}</div>
          </div>
          
          <div className="breathing-progress">
            <div className="round-indicator">
              Ronda {currentRound} de {rounds}
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ 
                  width: `${((currentRound - 1) / rounds) * 100}%` 
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BreathingExerciseStep; 